import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityCompanyView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { OpportunityService } from '@http/clients/opportunity.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    ObjectExtensionsService,
    SnackbarNotificationService
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

interface IModalEditOpportunityCompanyParameters {
    company: OpportunityCompanyView;
}

@Component({
    selector: 'app-modal-edit-opportunity-company',
    templateUrl: './modal-edit-opportunity-company.component.html',
    styleUrls: ['./modal-edit-opportunity-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalEditOpportunityCompanyComponent
    extends ModalComponentBase<IModalEditOpportunityCompanyParameters, OpportunityCompanyView>
    implements OnInit
{
    protected override parameters!: IModalEditOpportunityCompanyParameters;

    companyName!: string;

    company!: OpportunityCompanyView;

    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    companyInitialState!: OpportunityCompanyView;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly opportunityService: OpportunityService,
        private readonly awaiter: AwaiterService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly objectExtensionService: ObjectExtensionsService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.configItems = [];
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.companyName = this.parameters?.company.companyName ?? '';
        this.company = this.objectExtensionService.clone(this.parameters?.company) as OpportunityCompanyView;
        this.companyInitialState = this.objectExtensionService.clone(
            this.parameters?.company
        ) as OpportunityCompanyView;
        this.configItems = [
            {
                label: 'Roles',
                componentId: EditorType.entity,
                fieldName: 'opportunityCompanyRoles',
                entityId: 'opportunityBuildingContactRole',
                bindContext: this.company,
                additionalInfo: {
                    entityType: EntityTypes.Company && EntityTypes.Contact
                }
            }
        ];
    }

    async saveCompany(): Promise<void> {
        await this.awaiter.awaitAction(
            'Saving Company...',
            async () => {
                const opportunityCompanySaved = await this.opportunityService.saveCompany(this.company);

                if (opportunityCompanySaved) {
                    this.snackbarNotificationService.success('Company saved successfully.');
                    this.close(opportunityCompanySaved);
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    onClose() {
        this.company = this.companyInitialState;
        this.close(this.companyInitialState);
    }
}
